import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { CheckboxSegmented } from '@components/CheckboxSegmented';
import TimeZone from '@components/TimeZone';
import { Alert, Col, Form, Input, notification, Row, Table } from 'antd';
import moment from 'moment';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import { DAYS_OF_WEEK, FORM_LAYOUT_DEFAULT } from '@src/constants/commons';
import { ALLOWED_ROLES, getFullUrl, mergeAllowedRoles, useIsMobile } from '@src/packages/utils';
import ShiftService from '@src/services/ShiftService';
import IntlMessages from '@src/util/IntlMessages';

import ActiveDay, { validateActiveDayBreakFields } from '../../components/ActiveDay';
import ShiftPermission from '../../components/ShiftPermission';
import { PATHS, SHIFT_STATUS, SHIFT_TYPES } from '../../constants';
import { useShiftValidation } from '../../hooks/useShiftValidation';
import { useShiftValidationTranslations } from '../../hooks/useShiftValidationTranslations';
import type {
  ActiveDaySerialized,
  ActiveDayValue,
  OrganizationsReduxState,
  WorkJourneySummary,
} from '../../types';
import styles from '../styles.module.less';
import { DAYS_OF_WEEK_OPTIONS } from '../utils';
import { calculateTotalHours, formatRegulationName } from './utils';

const { Item, useForm } = Form;

export default function ShiftWorkday() {
  const { isMobile } = useIsMobile();
  const navigate = useHistory();
  const intl = useIntl();
  const [form] = useForm();
  const organizationId = useSelector(
    ({ organizations }: OrganizationsReduxState) => organizations?.organization?.id,
  );
  const { shiftId } = useParams<{ shiftId?: string }>();
  const [loading, setLoading] = React.useState(false);
  const [selectedDays, setSelectedDays] = React.useState<Array<string | number>>([]);
  const [totalHours, setTotalHours] = React.useState(0);
  const [workJourneys, setWorkJourneys] = React.useState<WorkJourneySummary[]>([]);

  const shiftService = useMemo(() => new ShiftService(), []);
  const hasAssociatedJourneys = workJourneys.length > 0 && !!shiftId;

  const {
    validateDaysRule,
    validateDays,
    validateActiveDay,
    getWarningsForDay,
    getErrorsForDay,
    hasWarnings,
    hasErrors,
    validateSundaysRule,
    validateSunday,
  } = useShiftValidation(SHIFT_TYPES.SHIFT_WORK, {}, form);
  const validationMessages = useShiftValidationTranslations();

  const getWorkJourneysByShiftId = async (id: string, orgId: string) => {
    const journeys = await shiftService.getWorkJourneysByShiftId(id, orgId);
    setWorkJourneys(journeys);
    return journeys;
  };

  const onSave = async () => {
    try {
      const values = form.getFieldsValue();

      const dayList = values.days || [];
      for (const day of dayList) {
        const dayState = form.getFieldValue(`day_${day}`);
        const breakFieldError = validateActiveDayBreakFields(dayState, intl);
        if (breakFieldError) {
          notification.error({
            description: breakFieldError,
          } as Parameters<typeof notification.error>[0]);
          return;
        }
      }

      setLoading(true);
      // Build days data - since days are an array of values, we need to build an object with the day as key
      const daysData = values.days.reduce((acc, day) => {
        acc[day] = form.getFieldValue(`day_${day}`);
        return acc;
      }, {} as Record<string | number, ActiveDayValue>);

      if (hasErrors()) {
        notification.error({
          description: intl.formatMessage({ id: 'general.hasErrors.message' }),
        } as Parameters<typeof notification.error>[0]);
        return;
      }

      if (shiftId) {
        await shiftService.updateWorkdayShift({
          ...values,
          organizationId,
          id: shiftId,
          status: SHIFT_STATUS.ACTIVE,
          daysData,
        });
      } else {
        await shiftService.createWorkdayShift({
          ...values,
          organizationId,
          status: SHIFT_STATUS.ACTIVE,
          daysData,
        });
      }

      notification.success({
        message: intl.formatMessage({ id: 'general.save.successful.message' }),
        description: intl.formatMessage({ id: 'general.save.successful.message' }),
      });
      navigate.push(getFullUrl(PATHS.BASE_URL, window.location.pathname));
    } catch (error) {
      notification.error({
        message: intl.formatMessage({ id: 'general.save.error.message' }),
      });
    } finally {
      setLoading(false);
    }
  };

  const updateRegulationName = React.useCallback(() => {
    const days = form.getFieldValue('days') || [];
    const formattedName = formatRegulationName(days, form);
    // Use setFields instead of setFieldsValue to avoid interfering with other field validations
    form.setFields([
      {
        name: 'regulationName',
        value: formattedName,
      },
    ]);
  }, [form]);

  const updateTotalHours = React.useCallback(() => {
    const days = form.getFieldValue('days') || [];
    const hours = calculateTotalHours(days, form);
    setTotalHours(hours);
  }, [form]);

  // Function that updates both the name and the total hours
  const updateAll = React.useCallback(() => {
    updateRegulationName();
    updateTotalHours();
  }, [updateRegulationName, updateTotalHours]);

  const handleDayChange = (day: string | number, values: ActiveDayValue) => {
    form.setFieldsValue({
      [`day_${day}`]: values,
    });
    // Pass the day key for day-specific warning validation
    validateActiveDay(values, day);
    // Update both the name and the total hours
    updateAll();
  };

  const updateSelectedDays = (days: Array<string | number>) => {
    setSelectedDays(days);

    // Reset days that are no longer selected
    const currentValues = form.getFieldsValue();
    const fieldsToUpdate = [];

    DAYS_OF_WEEK.forEach(({ value }) => {
      if (!days.includes(value) && currentValues[`day_${value}`]) {
        fieldsToUpdate.push({
          name: `day_${value}`,
          value: { isActive: false },
        });
      }
    });

    if (fieldsToUpdate.length > 0) {
      form.setFields(fieldsToUpdate);
    }

    // Update both the name and the total hours when the selected days change
    updateAll();
  };

  const onDaysChange = (days: Array<string | number>) => {
    validateDays(days);
    updateSelectedDays(days);
  };

  const tableColumns = [
    {
      title: <IntlMessages id="shifts.day" />,
      dataIndex: 'day',
      key: 'day',
      width: 100,
      render: (_: unknown, record: { day: string | number; dayName: string }) => (
        <div className="gx-font-weight-medium">{record.dayName}</div>
      ),
    },
    {
      title: <IntlMessages id="shifts.schedule.and.break" />,
      dataIndex: 'schedule',
      key: 'schedule',
      render: (_: unknown, record: { day: string | number; dayName: string }) => (
        <Item
          noStyle
          shouldUpdate={(prev, current) =>
            prev[`day_${record.day}`] !== current[`day_${record.day}`]
          }
        >
          {({ getFieldValue }) => (
            <ActiveDay
              disabled={hasAssociatedJourneys}
              key={record.day}
              includeActiveDay={false}
              value={getFieldValue(`day_${record.day}`)}
              onChange={values => handleDayChange(record.day, values)}
              className="gx-w-100"
              warnings={getWarningsForDay(record.day)} // Pass the day key for day-specific warning validation
              errors={getErrorsForDay(record.day)}
            />
          )}
        </Item>
      ),
    },
  ];

  // Get selected days from form or use empty array
  const formDays = form.getFieldValue('days') || [];

  // Create table data only for selected days
  const tableData = DAYS_OF_WEEK.filter(day => formDays.includes(day.value)).map(day => ({
    key: day.value,
    day: day.value,
    dayName: day.label,
  }));

  // Update all when the component mounts
  React.useEffect(() => {
    updateAll();
  }, [updateAll]);

  React.useEffect(() => {
    if (!shiftId) return;
    const fetchShift = async () => {
      setLoading(true);
      const shift = await shiftService.getShift(shiftId, organizationId);
      if (!shift) return;

      // Build individual day configurations from recurrence data
      const dayFields: Record<string, ActiveDayValue> = {};
      if (shift.daysOfWeek && shift.daysOfWeek.length > 0) {
        shift.daysOfWeek.forEach(dayOfWeek => {
          // Find the specific day data in recurrence structure
          const dayData = (shift?.recurrence?.weeks
            ?.flatMap(week => (week.days ? Object.entries(week.days) : []))
            ?.find(([day]) => parseInt(String(day), 10) === dayOfWeek)?.[1] ||
            {}) as Partial<ActiveDaySerialized>;

          // Set default values if day data doesn't exist
          const recurrenceData: ActiveDayValue = {
            isActive: dayData.isActive ?? true,
            startTime: dayData.startTime
              ? moment(dayData.startTime, 'HH:mm')
              : moment('08:00', 'HH:mm'),
            endTime: dayData.endTime ? moment(dayData.endTime, 'HH:mm') : moment('17:00', 'HH:mm'),
            breakTime: dayData.breakTime ?? null,
            startBreakTime: dayData.startBreakTime ? moment(dayData.startBreakTime, 'HH:mm') : null,
          };

          dayFields[`day_${dayOfWeek}`] = recurrenceData;
        });
      }

      let resolution = null;
      if (shift?.resolution?.name || shift?.resolution?.date) {
        resolution = {
          name: shift?.resolution?.name,
          date:
            shift?.resolution?.date &&
            typeof shift.resolution.date === 'object' &&
            'seconds' in shift.resolution.date
              ? moment.unix(shift.resolution.date.seconds)
              : null,
        };
      }

      // Set all form values including individual day configurations
      form.setFieldsValue({
        ...dayFields, // Set individual day configurations
        days: shift?.daysOfWeek,
        name: shift?.name,
        regulationName: shift?.regulationName,
        timeZone: shift?.timeZone,
        resolution,
        weekCount: shift?.weekCount,
      });

      setSelectedDays(shift.daysOfWeek || []);
      updateTotalHours();
      validateSunday(shift.daysOfWeek || []);
      validateDays(shift.daysOfWeek || []);
      setLoading(false);
    };
    fetchShift();
    getWorkJourneysByShiftId(shiftId, organizationId);
  }, [shiftId, organizationId, form, shiftService, updateTotalHours]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <BoxContainer>
      <BoxContainer content fixed bottomBorder>
        <FilterContainer
          goBack={() => navigate.goBack()}
          title={<Title.Header value={<IntlMessages id="shifts.create.shiftWork.title" />} />}
          buttonItems={[
            {
              iconName: 'save',
              action: () => form.submit(),
              disabled: loading,
              allowedRole: mergeAllowedRoles(
                ALLOWED_ROLES.ORGANIZATIONS.WORK_SHIFTS.CREATE,
                ALLOWED_ROLES.ORGANIZATIONS.WORK_SHIFTS.UPDATE,
              ),
            },
          ]}
        />
      </BoxContainer>
      <BoxContainer content loading={loading}>
        <Form
          form={form}
          name="shiftWorkday"
          layout={FORM_LAYOUT_DEFAULT as unknown as React.ComponentProps<typeof Form>['layout']}
          onFinish={onSave}
          initialValues={{
            weekCount: null,
            days: [],

            name: '',
            regulationName: '',
          }}
        >
          {hasAssociatedJourneys && (
            <Row>
              <Alert
                message={intl.formatMessage({ id: 'shifts.edit.hasAssociatedJourneys' })}
                type="warning"
                showIcon
              />
            </Row>
          )}
          <Row gutter={[8, 8]}>
            <Col xs={24} md={12} lg={12}>
              <Title.SubTitle value={<IntlMessages id="shifts.configuration" />} />
            </Col>
            <Col span={24}>
              <Title.LabelForm className="gx-mb-2" value={<IntlMessages id="shifts.days" />} />
              <Item
                name="days"
                rules={[
                  {
                    required: true,
                    message: validationMessages.daysRequired,
                  },
                  validateDaysRule(),
                  validateSundaysRule(),
                ]}
              >
                <CheckboxSegmented
                  disabled={hasAssociatedJourneys}
                  onChange={onDaysChange}
                  options={DAYS_OF_WEEK_OPTIONS}
                />
              </Item>
            </Col>
            <Col span={24}>
              <Table
                bordered
                className={styles.shiftTable}
                columns={tableColumns}
                dataSource={tableData}
                pagination={false}
                rowClassName={record =>
                  selectedDays.includes(record.day) ? '' : 'ant-table-row-disabled'
                }
                scroll={isMobile ? { x: 600 } : undefined}
                footer={() => (
                  <div className="gx-flex-row gx-guarnic-gap-2">
                    <div className="gx-font-weight-bold">
                      <IntlMessages id="shifts.weeks.hours.estimated" />
                    </div>
                    <div>{totalHours.toFixed(2)}</div>
                  </div>
                )}
              />
            </Col>
            <Col xs={24} md={12} lg={12} xl={12}>
              <Title.LabelForm
                className="gx-mb-2"
                required
                value={<IntlMessages id="shifts.form.timezone" />}
              />
              <Item
                name="timeZone"
                rules={[
                  { required: true, message: validationMessages.timeZoneRequired },
                ]}
              >
                <TimeZone className="gx-w-100" disabled={hasAssociatedJourneys} />
              </Item>
            </Col>
          </Row>
          <Row gutter={[8, 8]}>
            <Col xs={24} md={12} lg={12} xl={12}>
              <Title.LabelForm
                className="gx-mb-2"
                required
                value={<IntlMessages id="shifts.form.name" />}
              />
              <Item
                name="name"
                rules={[
                  { required: true, message: validationMessages.nameRequired },
                ]}
              >
                <Input />
              </Item>
            </Col>
            <Col xs={24} md={12} lg={12} xl={12}>
              <Title.LabelForm
                className="gx-mb-2"
                value={<IntlMessages id="shifts.form.regulation.name" />}
              />
              <Item name="regulationName">
                <Input disabled />
              </Item>
            </Col>
          </Row>
          <ShiftPermission
            show={
              hasWarnings() ||
              !!form.getFieldValue('resolution')?.name ||
              !!form.getFieldValue('resolution')?.date
            }
          />
        </Form>
      </BoxContainer>
    </BoxContainer>
  );
}
